import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import type { RoomMode } from "@/engine/state/roomControl";
import {
  DeviceSettingsPanel,
  type SettingsSection,
} from "@/components/room/DeviceSettingsPanel";
import { AvatarIdentity } from "@/components/room/AvatarIdentity";
import { cn } from "@/engine/support/classNames";

/** One screen, one title: the dropdown says which section it opened. */
const SECTION_TITLES: Record<SettingsSection, string> = {
  profile: "settings.profile",
  application: "settings.application",
  devices: "settings.devices",
  contact: "settings.contactUs",
};

const SECTION_DESCRIPTIONS: Record<SettingsSection, string> = {
  profile: "Your avatar and the name others see.",
  application: "Room preferences and master volume.",
  devices: "Camera, microphone and audio output.",
  contact: "Send us a message about this room.",
};

export interface SettingsButtonProps {
  selectedAudioInput?: string;
  selectedVideoInput?: string;
  onAudioInputChange?: (deviceId: string) => void;
  onVideoInputChange?: (deviceId: string) => void;
  /** Visual style of the trigger button */
  variant?: "ghost" | "outline" | "secondary";
  /** Size of the trigger button. Default matches the room control bar. */
  size?: "sm" | "lg";
  /** Whether to show a text label beside the icon. Hidden on very small screens. */
  showLabel?: boolean;
  className?: string;
  id?: string;
  /** Room the contact form attaches its message to. */
  roomId?: string;
  /** Controlled open state. When omitted the component still self-opens. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Which section the modal shows. */
  section?: SettingsSection;
  /**
   * What kind of room this is being opened from. Absent on the entry screen,
   * where the visitor is choosing how their rooms behave rather than reading
   * what one of them does.
   */
  roomMode?: RoomMode;
  /** When true, the trigger button is not rendered. Useful when an external
   * component (e.g. SettingsMenu) owns the trigger. */
  hideTrigger?: boolean;
}

/** Controlled when `open` is passed, self-managed otherwise. */
function useControlledOpen(
  open: boolean | undefined,
  onOpenChange?: (open: boolean) => void,
) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const currentOpen = isControlled ? open : internalOpen;
  const setCurrentOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  return { currentOpen, setCurrentOpen };
}

type TriggerProps = Pick<
  SettingsButtonProps,
  "id" | "variant" | "size" | "showLabel" | "className"
> & { onOpen: () => void };

/** The gear button; its sizing tracks `showLabel` and the `lg` bar variant. */
function SettingsTrigger({
  id,
  variant = "ghost",
  size = "sm",
  showLabel = false,
  className = "",
  onOpen,
}: TriggerProps) {
  const { t } = useTranslation();
  return (
    <Button
      id={id}
      type="button"
      variant={variant}
      size={showLabel ? "sm" : "icon"}
      aria-label={t("settings.title")}
      title={t("settings.title")}
      data-testid="control-settings"
      onClick={(e) => {
        e.preventDefault();
        onOpen();
      }}
      className={cn(
        "rounded-full",
        showLabel
          ? "gap-1.5 px-3 h-9 text-sm font-medium"
          : size === "lg"
            ? "h-14 w-14"
            : "h-9 w-9 sm:h-10 sm:w-10",
        className,
      )}
    >
      <Settings
        className={cn(
          "shrink-0",
          showLabel
            ? "w-4 h-4"
            : size === "lg"
              ? "w-6 h-6"
              : "w-4 h-4 sm:w-5 sm:h-5",
        )}
      />
      {showLabel && <span>{t("settings.title")}</span>}
    </Button>
  );
}

interface SurfaceProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: SettingsSection;
  title: string;
  description: string;
  panel: ReactNode;
}

/** Full-screen bottom sheet used on phones. */
function MobileSettings({
  open,
  onOpenChange,
  section,
  title,
  description,
  panel,
}: SurfaceProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        // `inset-0` pins the sheet to the real visual viewport, so mobile
        // browser chrome cannot push the footer of the panel off-screen.
        // It leaves at once, for the reason the desktop surface gives below.
        className="inset-0 h-full w-full max-w-none rounded-none border-0 p-0 flex flex-col gap-0 data-[state=closed]:animate-none!"
      >
        <SheetHeader className="shrink-0 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] border-b border-border text-left">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription className="sr-only">{description}</SheetDescription>
        </SheetHeader>
        {section === "profile" && (
          <div className="shrink-0 px-4 pt-3 pb-1">
            <AvatarIdentity />
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {panel}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** Centred dialog used on desktop. */
function DesktopSettings({
  open,
  onOpenChange,
  section,
  title,
  description,
  panel,
}: SurfaceProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* It leaves at once rather than fading: a modal holds the page's pointer
          events for as long as it is in the tree, so an exit animation is a
          window in which every click is swallowed — including the one on the
          control that would open it again (#572, `specs/motion`). */}
      <DialogContent className="p-0 gap-0 overflow-hidden data-[state=closed]:animate-none! sm:max-w-md">
        <DialogHeader className="px-5 py-4 border-b border-border text-left">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">
            {description}
          </DialogDescription>
        </DialogHeader>
        {section === "profile" && (
          <div className="px-5 pt-4 pb-2">
            <AvatarIdentity />
          </div>
        )}
        <div className="max-h-[80vh] overflow-y-auto px-5 py-4">{panel}</div>
      </DialogContent>
    </Dialog>
  );
}

const noop = () => {};

const SETTINGS_DEFAULTS = {
  onAudioInputChange: noop,
  onVideoInputChange: noop,
  variant: "ghost",
  size: "sm",
  showLabel: false,
  className: "",
  section: "profile",
  hideTrigger: false,
} satisfies Partial<SettingsButtonProps>;

export function SettingsButton(props: SettingsButtonProps) {
  const { t } = useTranslation();
  const {
    selectedAudioInput,
    selectedVideoInput,
    onAudioInputChange,
    onVideoInputChange,
    variant,
    size,
    showLabel,
    className,
    id,
    roomId,
    open,
    onOpenChange,
    section,
    roomMode,
    hideTrigger,
  } = { ...SETTINGS_DEFAULTS, ...props };
  const isMobile = useIsMobile();
  const { currentOpen, setCurrentOpen } = useControlledOpen(open, onOpenChange);

  const panel = (
    <DeviceSettingsPanel
      selectedAudioInput={selectedAudioInput}
      selectedVideoInput={selectedVideoInput}
      onAudioInputChange={onAudioInputChange}
      onVideoInputChange={onVideoInputChange}
      roomId={roomId}
      section={section}
      roomMode={roomMode}
    />
  );

  const surface: SurfaceProps = {
    open: currentOpen,
    onOpenChange: setCurrentOpen,
    section,
    title: t(SECTION_TITLES[section]),
    description: SECTION_DESCRIPTIONS[section],
    panel,
  };

  return (
    <>
      {!hideTrigger && (
        <SettingsTrigger
          id={id}
          variant={variant}
          size={size}
          showLabel={showLabel}
          className={className}
          onOpen={() => setCurrentOpen(true)}
        />
      )}
      {isMobile ? (
        <MobileSettings {...surface} />
      ) : (
        <DesktopSettings {...surface} />
      )}
    </>
  );
}
